import { c as _c } from "react/compiler-runtime";
// Founder workspace. NUMBERS-FIRST overview whose cards drill into the existing
// admin pages (decision 2A: the destination pages are unchanged — only the way
// in is new).
//
// The founder reviewed this and asked for an operational panel, not a pretty
// one: text over charts, seller metrics separated from buyer metrics, returns
// visible for today, and the applications counters mirrored here. See
// docs/plans/004-admin-founder-feedback.md.
//
// Every number comes from the mock provider; the gateway has no aggregate
// endpoints yet. See src/api/mock/dashboard.ts.
import { ArrowRight, BarChart3, Globe, PackageCheck, PackageX, Repeat, UserCog, ShoppingCart, Store, Table2, Timer, TrendingDown, TrendingUp, Users, Wallet } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Area, AreaChart } from "recharts";
import { type DashboardCard, type DashboardRange, type DashboardSection, type GeoFlowBuyer, type GeoFlowDestination, type GeoFlowOrigin, type Metric } from "@/api";
import { useDashboardOverview } from "@/api/hooks/use-dashboard";
import { useGeoFlows } from "@/api/hooks/use-analytics";
import { MetrikaSection } from "@/components/dashboard-metrika";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { RangePicker } from "@/components/range-picker";
import { RankedList } from "@/components/ranked-list";
import { Segmented } from "@/components/segmented";
import { StatBlock, type StatView } from "@/components/stat-block";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatCurrency, formatNumber } from "@osmix/formatters";
import { cn } from "@/lib/utils";
const shortDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", {
  day: "2-digit",
  month: "short"
});

/** Trend pill: green when up, red when down, muted when there's no baseline. */
function Delta(t0) {
  const $ = _c(11);
  const {
    metric
  } = t0;
  if (metric.delta === undefined) {
    return null;
  }
  const up = metric.delta >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const t1 = up ? "text-emerald-600 dark:text-emerald-500" : "text-destructive";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex items-center gap-1 text-xs font-medium", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== Icon) {
    t3 = <Icon className="size-3" />;
    $[2] = Icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = up ? "+" : "";
  const t5 = metric.delta * 100;
  let t6;
  if ($[4] !== t5) {
    t6 = t5.toFixed(1);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4 || $[9] !== t6) {
    t7 = <span className={t2}>{t3}{t4}{t6}%</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}

/** Per-card presentation: icon, accent colour, and how the value reads. */
const CARD_META: Record<string, {
  icon: typeof Users;
  color: string;
  format?: "money" | "ratio" | "percent" | "days";
}> = {
  sellers: {
    icon: Store,
    color: "var(--chart-2, #10b981)"
  },
  gmv: {
    icon: Wallet,
    color: "var(--chart-4, #f59e0b)",
    format: "money"
  },
  orders: {
    icon: ShoppingCart,
    color: "var(--chart-3, #0ea5e9)"
  },
  buyers: {
    icon: Users,
    color: "var(--chart-1, #6366f1)"
  },
  activeBuyers: {
    icon: Users,
    color: "var(--chart-1, #6366f1)"
  },
  aov: {
    icon: Wallet,
    color: "var(--chart-4, #f59e0b)",
    format: "money"
  },
  purchaseFrequency: {
    icon: Repeat,
    color: "var(--chart-5, #a855f7)",
    format: "ratio"
  },
  returns: {
    icon: PackageX,
    color: "var(--destructive, #ef4444)"
  },
  returnRate: {
    icon: PackageX,
    color: "var(--chart-4, #f59e0b)",
    format: "percent"
  },
  fulfilmentDays: {
    icon: Timer,
    color: "var(--chart-3, #0ea5e9)",
    format: "days"
  }
};
function MetricCard(t0) {
  const $ = _c(53);
  const {
    card,
    view,
    drillLabel
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== card.key) {
    t1 = CARD_META[card.key] ?? {
      icon: BarChart3,
      color: "var(--chart-1, #6366f1)"
    };
    $[0] = card.key;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const meta = t1;
  const Icon = meta.icon;
  const t2 = `dashboard.cards.${card.key}`;
  let t3;
  if ($[2] !== card.key || $[3] !== t || $[4] !== t2) {
    t3 = t(t2, card.key);
    $[2] = card.key;
    $[3] = t;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const title = t3;
  let t4;
  if ($[6] !== meta.format || $[7] !== t) {
    t4 = value => {
      if (meta.format === "money") {
        return formatCurrency(value, {
          maximumFractionDigits: 0
        });
      }
      if (meta.format === "ratio") {
        return t("dashboard.units.perMonth", {
          count: value
        });
      }
      if (meta.format === "percent") {
        return `${value}%`;
      }
      if (meta.format === "days") {
        return t("dashboard.units.days", {
          count: value
        });
      }
      return formatNumber(value);
    };
    $[6] = meta.format;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const format = t4;
  let t5;
  if ($[9] !== meta.color || $[10] !== title) {
    t5 = {
      value: {
        label: title,
        color: meta.color
      }
    };
    $[9] = meta.color;
    $[10] = title;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const config = t5;
  let t6;
  if ($[12] !== title) {
    t6 = <CardTitle className="text-sm font-medium">{title}</CardTitle>;
    $[12] = title;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== Icon) {
    t7 = <Icon className="size-4 text-muted-foreground" />;
    $[14] = Icon;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">{t6}{t7}</CardHeader>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== card.metric.value || $[20] !== format) {
    t9 = format(card.metric.value);
    $[19] = card.metric.value;
    $[20] = format;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t9) {
    t10 = <span className="text-2xl font-bold tabular-nums">{t9}</span>;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== card.metric) {
    t11 = <Delta metric={card.metric} />;
    $[24] = card.metric;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11) {
    t12 = <div className="flex items-baseline gap-2">{t10}{t11}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== card.total || $[30] !== t) {
    t13 = card.total !== undefined && <span className="text-xs text-muted-foreground">{t("dashboard.totalHint", {
        count: card.total,
        value: formatNumber(card.total)
      })}</span>;
    $[29] = card.total;
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t12 || $[33] !== t13) {
    t14 = <div className="flex flex-col gap-0.5">{t12}{t13}</div>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== card.key || $[36] !== card.series || $[37] !== config || $[38] !== format || $[39] !== meta.color || $[40] !== view) {
    t15 = view === "chart" && <ChartContainer height={96}><AreaChart data={card.series} margin={{
        top: 4,
        right: 0,
        bottom: 0,
        left: 0
      }}><defs><linearGradient id={`fill-${card.key}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={meta.color} stopOpacity={0.35} /><stop offset="100%" stopColor={meta.color} stopOpacity={0} /></linearGradient></defs><ChartTooltip config={config} formatter={format} labelFormatter={shortDate} /><Area type="monotone" dataKey="value" stroke={meta.color} strokeWidth={2} fill={`url(#fill-${card.key})`} isAnimationActive={false} /></AreaChart></ChartContainer>;
    $[35] = card.key;
    $[36] = card.series;
    $[37] = config;
    $[38] = format;
    $[39] = meta.color;
    $[40] = view;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ArrowRight className="size-3.5" />;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== card.href || $[44] !== drillLabel) {
    t17 = <Button asChild={true} variant="ghost" size="sm" className="justify-start px-0 text-muted-foreground"><Link to={card.href}>{drillLabel}{t16}</Link></Button>;
    $[43] = card.href;
    $[44] = drillLabel;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t14 || $[47] !== t15 || $[48] !== t17) {
    t18 = <CardContent className="flex flex-col gap-3">{t14}{t15}{t17}</CardContent>;
    $[46] = t14;
    $[47] = t15;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t18 || $[51] !== t8) {
    t19 = <Card className="overflow-hidden">{t8}{t18}</Card>;
    $[50] = t18;
    $[51] = t8;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  return t19;
}

/** Which slice of the business the metrics area is showing. */
type Audience = "seller" | "buyer" | "quality" | "managers";
export function DashboardPage() {
  const $ = _c(79);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      kind: "allTime"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [range, setRange] = useState(t0);
  const [view, setView] = useState("text");
  const [audience, setAudience] = useState("seller");
  const {
    data,
    error
  } = useDashboardOverview(range);
  if (error) {
    let t1;
    if ($[1] !== error.message) {
      t1 = <p className="text-destructive">{error.message}</p>;
      $[1] = error.message;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (!data) {
    let t1;
    if ($[3] !== t) {
      t1 = t("common.loading");
      $[3] = t;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t1) {
      t2 = <p className="text-muted-foreground">{t1}</p>;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      "/users": "dashboard.drill.users",
      "/shops": "dashboard.drill.shops",
      "/products": "dashboard.drill.products",
      "/applications": "dashboard.drill.applications",
      "/orders": "dashboard.drill.orders",
      "/platform-roles": "dashboard.drill.platformRoles"
    };
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const DRILL_LABELS = t1;
  let t2;
  if ($[8] !== t) {
    t2 = href => t(DRILL_LABELS[href] ?? "dashboard.drill.orders");
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const drillFor = t2;
  let t3;
  if ($[10] !== t) {
    t3 = t("dashboard.title");
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <h1 data-tour="dashboard-intro" className="text-2xl font-bold tracking-tight">{t3}</h1>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t) {
    t5 = t("dashboard.description");
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <p className="text-sm text-muted-foreground">{t5}</p>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <div>{t4}{t6}</div>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t) {
    t8 = t("dashboard.view.text");
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = {
      value: "text",
      label: t8,
      icon: Table2
    };
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t) {
    t10 = t("dashboard.view.chart");
    $[25] = t;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = {
      value: "chart",
      label: t10,
      icon: BarChart3
    };
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t9) {
    t12 = [t9, t11];
    $[29] = t11;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12 || $[33] !== view) {
    t13 = <Segmented value={view} onChange={setView} options={t12} />;
    $[32] = t12;
    $[33] = view;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== range) {
    t14 = <RangePicker value={range} onChange={setRange} />;
    $[35] = range;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t13 || $[38] !== t14) {
    t15 = <div data-tour="dashboard-controls" className="flex flex-wrap items-center gap-2">{t13}{t14}</div>;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t15 || $[41] !== t7) {
    t16 = <div className="flex flex-wrap items-start justify-between gap-4">{t7}{t15}</div>;
    $[40] = t15;
    $[41] = t7;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t) {
    t17 = t("dashboard.sections.sellers");
    $[43] = t;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t17) {
    t18 = {
      value: "seller",
      label: t17,
      icon: Store
    };
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t) {
    t19 = t("dashboard.sections.buyers");
    $[47] = t;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t19) {
    t20 = {
      value: "buyer",
      label: t19,
      icon: Users
    };
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t) {
    t21 = t("dashboard.sections.quality");
    $[51] = t;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t21) {
    t22 = {
      value: "quality",
      label: t21,
      icon: PackageX
    };
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t) {
    t23 = t("dashboard.sections.managers");
    $[55] = t;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t23) {
    t24 = {
      value: "managers",
      label: t23,
      icon: UserCog
    };
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t18 || $[60] !== t20 || $[61] !== t22 || $[62] !== t24) {
    t25 = [t18, t20, t22, t24];
    $[59] = t18;
    $[60] = t20;
    $[61] = t22;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== audience || $[65] !== t25) {
    t26 = <div data-tour="dashboard-audience" className="max-w-full self-start"><Segmented value={audience} onChange={setAudience} options={t25} /></div>;
    $[64] = audience;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  const t27 = data[audience];
  let t28;
  if ($[67] !== drillFor || $[68] !== t27 || $[69] !== view) {
    t28 = <div data-tour="dashboard-metrics"><AudienceMetrics section={t27} view={view} drillFor={drillFor} /></div>;
    $[67] = drillFor;
    $[68] = t27;
    $[69] = view;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== t26 || $[72] !== t28) {
    t29 = <section className="flex flex-col gap-4">{t26}{t28}</section>;
    $[71] = t26;
    $[72] = t28;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  let t31;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <GeoFlowsSection />;
    t31 = <MetrikaSection />;
    $[74] = t30;
    $[75] = t31;
  } else {
    t30 = $[74];
    t31 = $[75];
  }
  let t32;
  if ($[76] !== t16 || $[77] !== t29) {
    t32 = <div className="flex flex-col gap-6">{t16}{t29}{t30}{t31}</div>;
    $[76] = t16;
    $[77] = t29;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  return t32;
}
const GEO_DAYS = [30, 90, 365] as const;
type GeoDays = (typeof GEO_DAYS)[number];

/** Одна строка рейтинга региона с полосой пропорции против максимума. */
function GeoRegionRow(t0) {
  const $ = _c(17);
  const {
    region,
    primary,
    secondary,
    share
  } = t0;
  let t1;
  if ($[0] !== region) {
    t1 = <span className="min-w-0 truncate text-sm">{region}</span>;
    $[0] = region;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== primary) {
    t2 = <span className="text-sm font-semibold tabular-nums">{primary}</span>;
    $[2] = primary;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== secondary) {
    t3 = <span className="text-xs text-muted-foreground tabular-nums">{secondary}</span>;
    $[4] = secondary;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="flex shrink-0 items-baseline gap-2 text-right">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className="flex items-baseline justify-between gap-4">{t1}{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `${Math.max(2, share * 100)}%`;
  let t7;
  if ($[12] !== t6) {
    t7 = <div className="h-1 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary/30" style={{
        width: t6
      }} /></div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t7) {
    t8 = <div className="flex flex-col gap-1 border-b py-1.5 last:border-0">{t5}{t7}</div>;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

/** Геоаналитика потоков — карточка дашборда (три панели: откуда/куда/потоки). */
function GeoFlowsSection() {
  const $ = _c(173);
  const {
    t
  } = useTranslation();
  const [days, setDays] = useState(365);
  const [destBy, setDestBy] = useState("city");
  const query = useGeoFlows(days);
  const data = query.data;
  let T0;
  let T1;
  let flows;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== data?.buyers || $[1] !== data?.destinationDistricts || $[2] !== data?.destinationRegions || $[3] !== data?.destinations || $[4] !== data?.flows || $[5] !== data?.origins || $[6] !== days || $[7] !== destBy || $[8] !== query.isPending || $[9] !== t) {
    const origins = data?.origins ?? [];
    const destinations = (destBy === "district" ? data?.destinationDistricts : destBy === "region" ? data?.destinationRegions : data?.destinations) ?? [];
    const buyers = data?.buyers ?? [];
    let t10;
    if ($[23] !== data?.flows) {
      t10 = data?.flows ?? [];
      $[23] = data?.flows;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    flows = t10;
    const maxOriginGmv = Math.max(...origins.map(_temp), 1);
    const maxDestGmv = Math.max(...destinations.map(_temp2), 1);
    const maxBuyerCount = Math.max(...buyers.map(_temp3), 1);
    T1 = Card;
    t8 = "overflow-hidden";
    let t11;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Globe className="size-4 text-muted-foreground" />;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== t) {
      t12 = t("geoAnalytics.title");
      $[26] = t;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== t12) {
      t13 = <CardTitle className="flex items-center gap-2 text-base">{t11}{t12}</CardTitle>;
      $[28] = t12;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== t) {
      t14 = t("geoAnalytics.description");
      $[30] = t;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== t14) {
      t15 = <p className="text-sm text-muted-foreground">{t14}</p>;
      $[32] = t14;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] !== t13 || $[35] !== t15) {
      t16 = <div>{t13}{t15}</div>;
      $[34] = t13;
      $[35] = t15;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    const t17 = `${days}`;
    let t18;
    if ($[37] !== t) {
      t18 = GEO_DAYS.map(value => ({
        value: `${value}`,
        label: t("geoAnalytics.days", {
          count: value
        })
      }));
      $[37] = t;
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    let t19;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = value_0 => setDays(Number(value_0) as GeoDays);
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    let t20;
    if ($[40] !== t17 || $[41] !== t18) {
      t20 = <Segmented value={t17} options={t18} onChange={t19} />;
      $[40] = t17;
      $[41] = t18;
      $[42] = t20;
    } else {
      t20 = $[42];
    }
    if ($[43] !== t16 || $[44] !== t20) {
      t9 = <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 border-b">{t16}{t20}</CardHeader>;
      $[43] = t16;
      $[44] = t20;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    T0 = CardContent;
    t5 = "grid gap-5 pt-4 lg:grid-cols-2";
    let t21;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <Store className="size-4 text-muted-foreground" />;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    let t22;
    if ($[47] !== t) {
      t22 = t("geoAnalytics.origins.title");
      $[47] = t;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    let t23;
    if ($[49] !== t22) {
      t23 = <div className="mb-1 flex items-center gap-2 text-sm font-medium">{t21}{t22}</div>;
      $[49] = t22;
      $[50] = t23;
    } else {
      t23 = $[50];
    }
    let t24;
    if ($[51] !== t) {
      t24 = t("geoAnalytics.origins.hint");
      $[51] = t;
      $[52] = t24;
    } else {
      t24 = $[52];
    }
    let t25;
    if ($[53] !== t24) {
      t25 = <p className="pb-1 text-xs text-muted-foreground">{t24}</p>;
      $[53] = t24;
      $[54] = t25;
    } else {
      t25 = $[54];
    }
    let t26;
    if ($[55] !== query.isPending || $[56] !== t) {
      t26 = query.isPending && <p className="py-4 text-sm text-muted-foreground">{t("geoAnalytics.loading")}</p>;
      $[55] = query.isPending;
      $[56] = t;
      $[57] = t26;
    } else {
      t26 = $[57];
    }
    const t27 = !query.isPending && origins.length === 0 && <p className="py-4 text-sm text-muted-foreground">{t("geoAnalytics.empty")}</p>;
    let t28;
    if ($[58] !== maxOriginGmv || $[59] !== t) {
      t28 = origin => <GeoRegionRow key={origin.region} region={origin.region} primary={t("geoAnalytics.origins.sellers", {
        count: origin.sellers
      })} secondary={formatCurrency(origin.gmv, {
        maximumFractionDigits: 0
      })} share={origin.gmv / maxOriginGmv} />;
      $[58] = maxOriginGmv;
      $[59] = t;
      $[60] = t28;
    } else {
      t28 = $[60];
    }
    const t29 = origins.slice(0, 6).map(t28);
    if ($[61] !== t23 || $[62] !== t25 || $[63] !== t26 || $[64] !== t27 || $[65] !== t29) {
      t6 = <div>{t23}{t25}{t26}{t27}{t29}</div>;
      $[61] = t23;
      $[62] = t25;
      $[63] = t26;
      $[64] = t27;
      $[65] = t29;
      $[66] = t6;
    } else {
      t6 = $[66];
    }
    let t30;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <PackageCheck className="size-4 text-muted-foreground" />;
      $[67] = t30;
    } else {
      t30 = $[67];
    }
    let t31;
    if ($[68] !== t) {
      t31 = t("geoAnalytics.destinations.title");
      $[68] = t;
      $[69] = t31;
    } else {
      t31 = $[69];
    }
    let t32;
    if ($[70] !== t31) {
      t32 = <div className="mb-1 flex items-center gap-2 text-sm font-medium">{t30}{t31}</div>;
      $[70] = t31;
      $[71] = t32;
    } else {
      t32 = $[71];
    }
    let t33;
    if ($[72] !== t) {
      t33 = t("geoAnalytics.destinations.hint");
      $[72] = t;
      $[73] = t33;
    } else {
      t33 = $[73];
    }
    let t34;
    if ($[74] !== t33) {
      t34 = <p className="pb-1 text-xs text-muted-foreground">{t33}</p>;
      $[74] = t33;
      $[75] = t34;
    } else {
      t34 = $[75];
    }
    let t35;
    if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = ["city", "region", "district"];
      $[76] = t35;
    } else {
      t35 = $[76];
    }
    let t36;
    if ($[77] !== t) {
      t36 = (t35 as const).map(value_1 => ({
        value: value_1,
        label: t(`geoAnalytics.destinations.by.${value_1}`)
      }));
      $[77] = t;
      $[78] = t36;
    } else {
      t36 = $[78];
    }
    let t37;
    if ($[79] !== destBy || $[80] !== t36) {
      t37 = <div className="pb-1"><Segmented value={destBy} options={t36} onChange={setDestBy} /></div>;
      $[79] = destBy;
      $[80] = t36;
      $[81] = t37;
    } else {
      t37 = $[81];
    }
    let t38;
    if ($[82] !== query.isPending || $[83] !== t) {
      t38 = query.isPending && <p className="py-4 text-sm text-muted-foreground">{t("geoAnalytics.loading")}</p>;
      $[82] = query.isPending;
      $[83] = t;
      $[84] = t38;
    } else {
      t38 = $[84];
    }
    const t39 = !query.isPending && destinations.length === 0 && <p className="py-4 text-sm text-muted-foreground">{t("geoAnalytics.empty")}</p>;
    let t40;
    if ($[85] !== maxDestGmv || $[86] !== t) {
      t40 = dest => <GeoRegionRow key={dest.region} region={dest.region} primary={t("geoAnalytics.destinations.orders", {
        count: dest.orders
      })} secondary={formatCurrency(dest.gmv, {
        maximumFractionDigits: 0
      })} share={dest.gmv / maxDestGmv} />;
      $[85] = maxDestGmv;
      $[86] = t;
      $[87] = t40;
    } else {
      t40 = $[87];
    }
    const t41 = destinations.slice(0, destBy === "district" ? 9 : 6).map(t40);
    if ($[88] !== t32 || $[89] !== t34 || $[90] !== t37 || $[91] !== t38 || $[92] !== t39 || $[93] !== t41) {
      t7 = <div>{t32}{t34}{t37}{t38}{t39}{t41}</div>;
      $[88] = t32;
      $[89] = t34;
      $[90] = t37;
      $[91] = t38;
      $[92] = t39;
      $[93] = t41;
      $[94] = t7;
    } else {
      t7 = $[94];
    }
    let t42;
    if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
      t42 = <Users className="size-4 text-muted-foreground" />;
      $[95] = t42;
    } else {
      t42 = $[95];
    }
    let t43;
    if ($[96] !== t) {
      t43 = t("geoAnalytics.buyers.title");
      $[96] = t;
      $[97] = t43;
    } else {
      t43 = $[97];
    }
    if ($[98] !== t43) {
      t0 = <div className="mb-1 flex items-center gap-2 text-sm font-medium">{t42}{t43}</div>;
      $[98] = t43;
      $[99] = t0;
    } else {
      t0 = $[99];
    }
    let t44;
    if ($[100] !== t) {
      t44 = t("geoAnalytics.buyers.hint");
      $[100] = t;
      $[101] = t44;
    } else {
      t44 = $[101];
    }
    if ($[102] !== t44) {
      t1 = <p className="pb-1 text-xs text-muted-foreground">{t44}</p>;
      $[102] = t44;
      $[103] = t1;
    } else {
      t1 = $[103];
    }
    if ($[104] !== query.isPending || $[105] !== t) {
      t2 = query.isPending && <p className="py-4 text-sm text-muted-foreground">{t("geoAnalytics.loading")}</p>;
      $[104] = query.isPending;
      $[105] = t;
      $[106] = t2;
    } else {
      t2 = $[106];
    }
    t3 = !query.isPending && buyers.length === 0 && <p className="py-4 text-sm text-muted-foreground">{t("geoAnalytics.empty")}</p>;
    let t45;
    if ($[107] !== maxBuyerCount || $[108] !== t) {
      t45 = buyer => <GeoRegionRow key={buyer.region} region={buyer.region} primary={t("geoAnalytics.buyers.count", {
        count: buyer.count
      })} secondary="" share={buyer.count / maxBuyerCount} />;
      $[107] = maxBuyerCount;
      $[108] = t;
      $[109] = t45;
    } else {
      t45 = $[109];
    }
    t4 = buyers.slice(0, 6).map(t45);
    $[0] = data?.buyers;
    $[1] = data?.destinationDistricts;
    $[2] = data?.destinationRegions;
    $[3] = data?.destinations;
    $[4] = data?.flows;
    $[5] = data?.origins;
    $[6] = days;
    $[7] = destBy;
    $[8] = query.isPending;
    $[9] = t;
    $[10] = T0;
    $[11] = T1;
    $[12] = flows;
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    T0 = $[10];
    T1 = $[11];
    flows = $[12];
    t0 = $[13];
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t10;
  if ($[110] !== t0 || $[111] !== t1 || $[112] !== t2 || $[113] !== t3 || $[114] !== t4) {
    t10 = <div>{t0}{t1}{t2}{t3}{t4}</div>;
    $[110] = t0;
    $[111] = t1;
    $[112] = t2;
    $[113] = t3;
    $[114] = t4;
    $[115] = t10;
  } else {
    t10 = $[115];
  }
  let t11;
  if ($[116] !== t) {
    t11 = t("geoAnalytics.flows.title");
    $[116] = t;
    $[117] = t11;
  } else {
    t11 = $[117];
  }
  let t12;
  if ($[118] !== t11) {
    t12 = <div className="mb-2 text-sm font-medium">{t11}</div>;
    $[118] = t11;
    $[119] = t12;
  } else {
    t12 = $[119];
  }
  let t13;
  if ($[120] !== t) {
    t13 = t("geoAnalytics.flows.from");
    $[120] = t;
    $[121] = t13;
  } else {
    t13 = $[121];
  }
  let t14;
  if ($[122] !== t13) {
    t14 = <TableHead>{t13}</TableHead>;
    $[122] = t13;
    $[123] = t14;
  } else {
    t14 = $[123];
  }
  let t15;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <TableHead className="w-8" />;
    $[124] = t15;
  } else {
    t15 = $[124];
  }
  let t16;
  if ($[125] !== t) {
    t16 = t("geoAnalytics.flows.to");
    $[125] = t;
    $[126] = t16;
  } else {
    t16 = $[126];
  }
  let t17;
  if ($[127] !== t16) {
    t17 = <TableHead>{t16}</TableHead>;
    $[127] = t16;
    $[128] = t17;
  } else {
    t17 = $[128];
  }
  let t18;
  if ($[129] !== t) {
    t18 = t("geoAnalytics.flows.orders");
    $[129] = t;
    $[130] = t18;
  } else {
    t18 = $[130];
  }
  let t19;
  if ($[131] !== t18) {
    t19 = <TableHead className="text-right">{t18}</TableHead>;
    $[131] = t18;
    $[132] = t19;
  } else {
    t19 = $[132];
  }
  let t20;
  if ($[133] !== t) {
    t20 = t("geoAnalytics.flows.gmv");
    $[133] = t;
    $[134] = t20;
  } else {
    t20 = $[134];
  }
  let t21;
  if ($[135] !== t20) {
    t21 = <TableHead className="text-right">{t20}</TableHead>;
    $[135] = t20;
    $[136] = t21;
  } else {
    t21 = $[136];
  }
  let t22;
  if ($[137] !== t14 || $[138] !== t17 || $[139] !== t19 || $[140] !== t21) {
    t22 = <TableHeader className="bg-muted/40"><TableRow>{t14}{t15}{t17}{t19}{t21}</TableRow></TableHeader>;
    $[137] = t14;
    $[138] = t17;
    $[139] = t19;
    $[140] = t21;
    $[141] = t22;
  } else {
    t22 = $[141];
  }
  let t23;
  if ($[142] !== query.isPending || $[143] !== t) {
    t23 = query.isPending && <TableRow><TableCell colSpan={5} className="h-16 text-center text-muted-foreground">{t("geoAnalytics.loading")}</TableCell></TableRow>;
    $[142] = query.isPending;
    $[143] = t;
    $[144] = t23;
  } else {
    t23 = $[144];
  }
  let t24;
  if ($[145] !== flows.length || $[146] !== query.isPending || $[147] !== t) {
    t24 = !query.isPending && flows.length === 0 && <TableRow><TableCell colSpan={5} className="h-16 text-center text-muted-foreground">{t("geoAnalytics.empty")}</TableCell></TableRow>;
    $[145] = flows.length;
    $[146] = query.isPending;
    $[147] = t;
    $[148] = t24;
  } else {
    t24 = $[148];
  }
  let t25;
  if ($[149] !== flows) {
    t25 = flows.map(_temp4);
    $[149] = flows;
    $[150] = t25;
  } else {
    t25 = $[150];
  }
  let t26;
  if ($[151] !== t23 || $[152] !== t24 || $[153] !== t25) {
    t26 = <TableBody>{t23}{t24}{t25}</TableBody>;
    $[151] = t23;
    $[152] = t24;
    $[153] = t25;
    $[154] = t26;
  } else {
    t26 = $[154];
  }
  let t27;
  if ($[155] !== t22 || $[156] !== t26) {
    t27 = <div className="max-h-64 overflow-auto rounded-lg border"><Table>{t22}{t26}</Table></div>;
    $[155] = t22;
    $[156] = t26;
    $[157] = t27;
  } else {
    t27 = $[157];
  }
  let t28;
  if ($[158] !== t12 || $[159] !== t27) {
    t28 = <div className="lg:col-span-2">{t12}{t27}</div>;
    $[158] = t12;
    $[159] = t27;
    $[160] = t28;
  } else {
    t28 = $[160];
  }
  let t29;
  if ($[161] !== T0 || $[162] !== t10 || $[163] !== t28 || $[164] !== t5 || $[165] !== t6 || $[166] !== t7) {
    t29 = <T0 className={t5}>{t6}{t7}{t10}{t28}</T0>;
    $[161] = T0;
    $[162] = t10;
    $[163] = t28;
    $[164] = t5;
    $[165] = t6;
    $[166] = t7;
    $[167] = t29;
  } else {
    t29 = $[167];
  }
  let t30;
  if ($[168] !== T1 || $[169] !== t29 || $[170] !== t8 || $[171] !== t9) {
    t30 = <T1 className={t8}>{t9}{t29}</T1>;
    $[168] = T1;
    $[169] = t29;
    $[170] = t8;
    $[171] = t9;
    $[172] = t30;
  } else {
    t30 = $[172];
  }
  return t30;
}

/** Cards + stat blocks for whichever audience tab is active. */
function _temp4(flow) {
  return <TableRow key={`${flow.fromRegion}→${flow.toRegion}`}><TableCell className="font-medium">{flow.fromRegion}</TableCell><TableCell className="text-muted-foreground"><ArrowRight className="size-4" /></TableCell><TableCell className="font-medium">{flow.toRegion}</TableCell><TableCell className="text-right tabular-nums">{formatNumber(flow.orders)}</TableCell><TableCell className="text-right tabular-nums">{formatCurrency(flow.gmv, {
        maximumFractionDigits: 0
      })}</TableCell></TableRow>;
}
function _temp3(b) {
  return b.count;
}
function _temp2(d) {
  return d.gmv;
}
function _temp(o) {
  return o.gmv;
}
function AudienceMetrics(t0) {
  const $ = _c(30);
  const {
    section,
    view,
    drillFor
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = section.cards.length % 3 === 0 ? "xl:grid-cols-3" : "xl:grid-cols-4";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("grid gap-4 md:grid-cols-2", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== drillFor || $[3] !== section.cards || $[4] !== view) {
    let t4;
    if ($[6] !== drillFor || $[7] !== view) {
      t4 = card => <MetricCard key={card.key} card={card} view={view} drillLabel={drillFor(card.href)} />;
      $[6] = drillFor;
      $[7] = view;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = section.cards.map(t4);
    $[2] = drillFor;
    $[3] = section.cards;
    $[4] = view;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== drillFor || $[13] !== section.blocks || $[14] !== t || $[15] !== view) {
    let t6;
    if ($[17] !== drillFor || $[18] !== t || $[19] !== view) {
      t6 = block => <StatBlock key={block.key} block={block} view={view} title={t(`dashboard.blocks.${block.key}`)} labelPrefix={`dashboard.rows.${block.key}`} noteKey="dashboard.staleNote" drillLabel={block.href ? drillFor(block.href) : undefined} />;
      $[17] = drillFor;
      $[18] = t;
      $[19] = view;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    t5 = section.blocks.map(t6);
    $[12] = drillFor;
    $[13] = section.blocks;
    $[14] = t;
    $[15] = view;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[21] !== section.lists || $[22] !== t) {
    t6 = section.lists?.map(list => <RankedList key={list.key} list={list} title={t(`dashboard.lists.${list.key}`)} reasonPrefix="dashboard.rows.returnReasons" />);
    $[21] = section.lists;
    $[22] = t;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <div className="grid gap-4 xl:grid-cols-2">{t5}{t6}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t4 || $[28] !== t7) {
    t8 = <div className="flex flex-col gap-4">{t4}{t7}</div>;
    $[27] = t4;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}